import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserHeader } from '../components/UserHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import MensajesP2P from '../components/MensajesP2P';
import {
  Mail, MailOpen, ArrowLeft, Send, Paperclip, X,
  ChevronLeft, Loader2, AlertCircle, RefreshCw, Plus, MessageSquare,
  Inbox, SendHorizontal, Users, Building2, User as UserIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { formatBsAs } from '../utils/fechaUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MensajesPage() {
  const navigate = useNavigate();
  const { token, isAuthenticated, user, isAdmin } = useAuth();

  // Admin <-> usuario
  const [mensajesAdmin, setMensajesAdmin] = useState([]);
  const [mensajesAdminEnviados, setMensajesAdminEnviados] = useState([]);
  // P2P agrupado por persona
  const [p2pRecibidos, setP2pRecibidos] = useState([]);
  const [p2pEnviados, setP2pEnviados] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedMensaje, setSelectedMensaje] = useState(null);
  const [mensajeDetalle, setMensajeDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [respuesta, setRespuesta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  // 'lista' | 'detalle' | 'nuevo' | 'p2p-chat'
  const [vista, setVista] = useState('lista');
  // 'directorio' | 'recibidos' | 'enviados'
  const [tab, setTab] = useState('recibidos');

  const [nuevoMensaje, setNuevoMensaje] = useState('');

  // Chat P2P unificado (todas las conversaciones con esa persona, sin filtro de curso)
  const [p2pChat, setP2pChat] = useState(null); // { otro_id, nombre, apellido, email }

  // ================= Fetchs =================
  const fetchTodo = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        fetch(`${API_URL}/api/mensajes-internos/usuario/bandeja`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/mensajes-internos/usuario/enviados`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/mensajes-internos/p2p/recibidos`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/mensajes-internos/p2p/enviados`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (r1.ok) {
        const d = await r1.json(); setMensajesAdmin(d.mensajes || []);
      }
      if (r2.ok) {
        const d = await r2.json(); setMensajesAdminEnviados(d.mensajes || []);
      }
      if (r3.ok) {
        const d = await r3.json(); setP2pRecibidos(d.mensajes || []);
      }
      if (r4.ok) {
        const d = await r4.json(); setP2pEnviados(d.mensajes || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated) fetchTodo();
  }, [isAuthenticated, fetchTodo]);

  // ================= Unificación =================
  const recibidosUnificados = useMemo(() => {
    const admin = mensajesAdmin.map(m => ({
      kind: 'admin',
      id: m.id,
      leido: m.leido,
      titulo: m.asunto || 'Mensaje de Administración',
      preview: m.contenido_preview || '',
      ts: m.created_at,
      adjuntos: m.adjuntos || [],
      remitente: m.remitente_email || 'Administración',
      _raw: m,
    }));
    const p2p = p2pRecibidos.map(m => ({
      kind: 'p2p',
      id: `p2p-${m.otro_id}`,
      leido: (m.no_leidos || 0) === 0,
      titulo: `${m.nombre || ''} ${m.apellido || ''}`.trim() || m.email || 'Compañere',
      preview: m.ultimo_mensaje || '',
      ts: m.ultimo_ts,
      adjuntos: [],
      no_leidos: m.no_leidos || 0,
      space_nombre: m.ultimo_space_nombre,
      otro_id: m.otro_id,
      remitente: m.email || '',
      _raw: m,
    }));
    return [...admin, ...p2p].sort((a, b) => {
      const ta = new Date(a.ts || 0).getTime();
      const tb = new Date(b.ts || 0).getTime();
      return tb - ta;
    });
  }, [mensajesAdmin, p2pRecibidos]);

  const enviadosUnificados = useMemo(() => {
    const admin = mensajesAdminEnviados.map(m => ({
      kind: 'admin',
      id: m.id,
      titulo: m.tipo_mensaje === 'respuesta'
        ? `Respuesta: ${m.asunto_hilo || 'Mensaje'}`
        : 'Mensaje a Administración',
      preview: m.contenido_preview || (m.contenido || '').replace(/<[^>]+>/g, '').substring(0, 150),
      ts: m.created_at,
      adjuntos: m.adjuntos || [],
      destinatario: 'Administración',
      leido_destino: m.leido_por_admin,
      _raw: m,
    }));
    const p2p = p2pEnviados.map(m => ({
      kind: 'p2p',
      id: `p2p-out-${m.otro_id}`,
      titulo: `${m.nombre || ''} ${m.apellido || ''}`.trim() || m.email || 'Compañere',
      preview: m.ultimo_mensaje || '',
      ts: m.ultimo_ts,
      adjuntos: [],
      space_nombre: m.ultimo_space_nombre,
      destinatario: m.email || '',
      otro_id: m.otro_id,
      leido_destino: m.ultimo_leido,
      _raw: m,
    }));
    return [...admin, ...p2p].sort((a, b) => {
      const ta = new Date(a.ts || 0).getTime();
      const tb = new Date(b.ts || 0).getTime();
      return tb - ta;
    });
  }, [mensajesAdminEnviados, p2pEnviados]);

  const totalNoLeidosRecibidos = useMemo(() => {
    const adminNL = mensajesAdmin.filter(m => !m.leido).length;
    const p2pNL = p2pRecibidos.reduce((acc, m) => acc + (m.no_leidos || 0), 0);
    return adminNL + p2pNL;
  }, [mensajesAdmin, p2pRecibidos]);

  // ================= Handlers =================
  const abrirItem = (item) => {
    if (item.kind === 'admin') {
      abrirMensajeAdmin(item._raw);
    } else {
      // P2P: abrir chat unificado con esa persona
      setP2pChat({
        otro_id: item.otro_id,
        nombre: item._raw.nombre,
        apellido: item._raw.apellido,
        email: item._raw.email,
      });
      setVista('p2p-chat');
    }
  };

  const abrirMensajeAdmin = async (mensaje) => {
    setSelectedMensaje(mensaje);
    setVista('detalle');
    setLoadingDetalle(true);
    setRespuesta('');
    setAdjuntos([]);
    try {
      const res = await fetch(`${API_URL}/api/mensajes-internos/usuario/${mensaje.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar mensaje');
      const data = await res.json();
      setMensajeDetalle(data);
      setMensajesAdmin(prev => prev.map(m => m.id === mensaje.id ? { ...m, leido: true } : m));
      window.dispatchEvent(new CustomEvent('mensajes-actualizados'));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const volverALista = () => {
    setVista('lista');
    setSelectedMensaje(null);
    setMensajeDetalle(null);
    setRespuesta('');
    setAdjuntos([]);
    setNuevoMensaje('');
    setP2pChat(null);
    fetchTodo();
  };

  const abrirNuevoMensaje = () => {
    setVista('nuevo');
    setNuevoMensaje('');
    setAdjuntos([]);
  };

  const enviarNuevoMensaje = async () => {
    if (!nuevoMensaje.trim()) {
      toast.error('Escribe un mensaje');
      return;
    }
    setEnviando(true);
    try {
      const res = await fetch(`${API_URL}/api/mensajes-internos/usuario/nuevo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contenido: nuevoMensaje, adjuntos: adjuntos })
      });
      if (!res.ok) throw new Error('Error al enviar mensaje');
      toast.success('Mensaje enviado a Administración');
      volverALista();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setEnviando(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/mensajes-internos/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error al subir archivo');
      }
      const data = await res.json();
      setAdjuntos(prev => [...prev, { nombre: data.nombre, url: data.url, tipo: data.tipo, size: data.size }]);
      toast.success('Archivo adjuntado');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const eliminarAdjunto = (index) => setAdjuntos(prev => prev.filter((_, i) => i !== index));

  const enviarRespuesta = async () => {
    if (!respuesta.trim()) {
      toast.error('Escribe un mensaje');
      return;
    }
    setEnviando(true);
    try {
      const res = await fetch(`${API_URL}/api/mensajes-internos/usuario/${selectedMensaje.id}/responder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contenido: respuesta, adjuntos: adjuntos })
      });
      if (!res.ok) throw new Error('Error al enviar respuesta');
      toast.success('Respuesta enviada');
      setRespuesta('');
      setAdjuntos([]);
      abrirMensajeAdmin(selectedMensaje);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setEnviando(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return formatBsAs(dateStr);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <UserHeader />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Debes iniciar sesión para ver tus mensajes</p>
              <Button onClick={() => navigate('/login')} className="mt-4">
                Iniciar sesión
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ============ Render lista item ============
  const renderItemRecibido = (item) => (
    <div
      key={item.id}
      onClick={() => abrirItem(item)}
      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${!item.leido ? 'bg-blue-50/50' : ''}`}
      data-testid={`mensaje-item-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-1">
          {item.kind === 'admin' ? (
            item.leido ? <MailOpen className="w-5 h-5 text-gray-400" /> : <Mail className="w-5 h-5 text-blue-600" />
          ) : (
            <UserIcon className={`w-5 h-5 ${!item.leido ? 'text-emerald-600' : 'text-gray-400'}`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <p className={`text-sm truncate ${!item.leido ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                {item.titulo}
              </p>
              {item.kind === 'admin' ? (
                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 flex-shrink-0">
                  <Building2 className="w-3 h-3" /> Admin
                </span>
              ) : (
                item.space_nombre && (
                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0">
                    <Users className="w-3 h-3" /> {item.space_nombre}
                  </span>
                )
              )}
            </div>
            <span className="text-xs text-gray-500 flex-shrink-0">{formatDate(item.ts)}</span>
          </div>
          <p className="text-sm text-gray-500 truncate mt-1">{item.preview}</p>
          <div className="flex items-center gap-2 mt-1">
            {item.adjuntos?.length > 0 && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <Paperclip className="w-3 h-3" />
                {item.adjuntos.length} adjunto{item.adjuntos.length > 1 ? 's' : ''}
              </span>
            )}
            {item.kind === 'p2p' && item.no_leidos > 0 && (
              <span className="bg-red-500 text-white text-[10px] rounded-full px-2 py-0.5">
                {item.no_leidos} nuevo{item.no_leidos > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderItemEnviado = (item) => (
    <div
      key={item.id}
      onClick={() => item.kind === 'p2p' ? abrirItem(item) : null}
      className={`p-4 ${item.kind === 'p2p' ? 'cursor-pointer hover:bg-gray-50' : ''} transition-colors`}
      data-testid={`mensaje-enviado-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-1">
          {item.kind === 'admin' ? (
            <Send className="w-5 h-5 text-green-500" />
          ) : (
            <SendHorizontal className="w-5 h-5 text-emerald-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <p className="text-sm font-medium text-gray-700 truncate">{item.titulo}</p>
              {item.kind === 'admin' ? (
                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 flex-shrink-0">
                  <Building2 className="w-3 h-3" /> Admin
                </span>
              ) : (
                item.space_nombre && (
                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0">
                    <Users className="w-3 h-3" /> {item.space_nombre}
                  </span>
                )
              )}
            </div>
            <span className="text-xs text-gray-500 flex-shrink-0">{formatDate(item.ts)}</span>
          </div>
          <p className="text-sm text-gray-500 truncate mt-1">{item.preview}</p>
          <div className="flex items-center gap-2 mt-1">
            {item.adjuntos?.length > 0 && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <Paperclip className="w-3 h-3" />
                {item.adjuntos.length} adjunto{item.adjuntos.length > 1 ? 's' : ''}
              </span>
            )}
            {item.leido_destino && (
              <span className="inline-flex items-center text-xs text-green-600">
                <MailOpen className="w-3 h-3 mr-1" />
                Leído
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <UserHeader />

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Barra superior */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            {(vista === 'detalle' || vista === 'nuevo' || vista === 'p2p-chat') ? (
              <Button variant="ghost" onClick={volverALista} className="gap-2" data-testid="volver-lista-btn">
                <ChevronLeft className="w-4 h-4" />
                Volver a mensajes
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => navigate('/')} className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Volver al inicio
              </Button>
            )}
          </div>
          {vista === 'lista' && tab !== 'directorio' && (
            <Button onClick={abrirNuevoMensaje} className="gap-2" data-testid="contactar-admin-btn">
              <Plus className="w-4 h-4" />
              Contactar Administración
            </Button>
          )}
        </div>

        {vista === 'lista' ? (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Mis Mensajes
              </CardTitle>
              <Button variant="outline" size="sm" onClick={fetchTodo} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </CardHeader>

            {/* Pestañas: Directorio | Recibidos | Enviados */}
            <div className="px-6 border-b">
              <div className="flex gap-4 overflow-x-auto">
                <button
                  onClick={() => setTab('directorio')}
                  className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    tab === 'directorio' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="tab-directorio"
                >
                  <Users className="w-4 h-4 inline mr-2" />
                  Directorio
                </button>
                <button
                  onClick={() => setTab('recibidos')}
                  className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    tab === 'recibidos' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="tab-recibidos"
                >
                  <Inbox className="w-4 h-4 inline mr-2" />
                  Recibidos
                  {totalNoLeidosRecibidos > 0 && (
                    <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5" data-testid="badge-recibidos-no-leidos">
                      {totalNoLeidosRecibidos}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setTab('enviados')}
                  className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    tab === 'enviados' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="tab-enviados"
                >
                  <SendHorizontal className="w-4 h-4 inline mr-2" />
                  Enviados
                  {enviadosUnificados.length > 0 && (
                    <span className="ml-2 bg-gray-200 text-gray-600 text-xs rounded-full px-2 py-0.5">
                      {enviadosUnificados.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            <CardContent className="pt-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                  <p className="text-gray-600">{error}</p>
                  <Button variant="outline" onClick={fetchTodo} className="mt-4">Reintentar</Button>
                </div>
              ) : tab === 'directorio' ? (
                <MensajesP2P
                  token={token}
                  currentUserId={user?.id}
                  isAdmin={isAdmin}
                />
              ) : tab === 'recibidos' ? (
                recibidosUnificados.length === 0 ? (
                  <div className="text-center py-12">
                    <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No tenés mensajes recibidos</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {recibidosUnificados.map(renderItemRecibido)}
                  </div>
                )
              ) : (
                enviadosUnificados.length === 0 ? (
                  <div className="text-center py-12">
                    <SendHorizontal className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No has enviado mensajes</p>
                    <Button onClick={abrirNuevoMensaje} className="mt-4 gap-2">
                      <Plus className="w-4 h-4" /> Enviar primer mensaje
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y">
                    {enviadosUnificados.map(renderItemEnviado)}
                  </div>
                )
              )}
            </CardContent>
          </Card>
        ) : vista === 'p2p-chat' ? (
          /* ============ CHAT P2P UNIFICADO ============ */
          <Card>
            <CardContent className="pt-6">
              <P2PUnifiedChat
                token={token}
                currentUserId={user?.id}
                otro={p2pChat}
                onClose={volverALista}
              />
            </CardContent>
          </Card>
        ) : vista === 'nuevo' ? (
          /* ============ NUEVO MENSAJE A ADMINISTRACIÓN ============ */
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Contactar a Administración
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Envía un mensaje a la administración del Colegio. Te responderemos a la brevedad.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tu mensaje</label>
                <textarea
                  value={nuevoMensaje}
                  onChange={(e) => setNuevoMensaje(e.target.value)}
                  placeholder="Escribe tu consulta o mensaje aquí..."
                  className="w-full p-3 border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  rows={6}
                  data-testid="nuevo-mensaje-textarea"
                />
              </div>
              {adjuntos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {adjuntos.map((adj, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-sm">
                      <Paperclip className="w-3 h-3" />
                      <span className="truncate max-w-[150px]">{adj.nombre}</span>
                      <button onClick={() => eliminarAdjunto(idx)} className="text-gray-500 hover:text-red-500">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between pt-4 border-t">
                <label className="cursor-pointer">
                  <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploadingFile} />
                  <span className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800">
                    {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                    Adjuntar archivo
                  </span>
                </label>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={volverALista}>Cancelar</Button>
                  <Button onClick={enviarNuevoMensaje} disabled={enviando || !nuevoMensaje.trim()} className="gap-2" data-testid="enviar-nuevo-mensaje-btn">
                    {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Enviar mensaje
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* ============ DETALLE DE MENSAJE ADMIN ============ */
          <Card>
            <CardContent className="pt-6">
              {loadingDetalle ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : mensajeDetalle ? (
                <div className="space-y-6">
                  <div className="border-b pb-4">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {mensajeDetalle.mensaje.asunto || 'Mensaje de Administración'}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      De: {mensajeDetalle.mensaje.remitente_email} • {formatDate(mensajeDetalle.mensaje.created_at)}
                    </p>
                  </div>
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: mensajeDetalle.mensaje.contenido }} />
                  {mensajeDetalle.mensaje.adjuntos?.length > 0 && (
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Archivos adjuntos:</p>
                      <div className="flex flex-wrap gap-2">
                        {mensajeDetalle.mensaje.adjuntos.map((adj, idx) => (
                          <a key={idx}
                            href={adj.url.startsWith('/') ? `${API_URL}${adj.url}` : adj.url}
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-md hover:bg-gray-200 text-sm">
                            <Paperclip className="w-4 h-4" />
                            {adj.nombre}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {mensajeDetalle.respuestas?.length > 0 && (
                    <div className="border-t pt-4 space-y-4">
                      <p className="text-sm font-medium text-gray-700">Conversación:</p>
                      {mensajeDetalle.respuestas.map((resp) => (
                        <div key={resp.id}
                          className={`p-4 rounded-lg ${resp.remitente_tipo === 'usuario' ? 'bg-blue-50 ml-8' : 'bg-gray-100 mr-8'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">
                              {resp.remitente_tipo === 'usuario' ? 'Tú' : 'Administración'}
                            </span>
                            <span className="text-xs text-gray-500">{formatDate(resp.created_at)}</span>
                          </div>
                          <div className="text-sm" dangerouslySetInnerHTML={{ __html: resp.contenido }} />
                          {resp.adjuntos?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {resp.adjuntos.map((adj, idx) => (
                                <a key={idx}
                                  href={adj.url.startsWith('/') ? `${API_URL}${adj.url}` : adj.url}
                                  target="_blank" rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                                  <Paperclip className="w-3 h-3" />
                                  {adj.nombre}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Responder:</p>
                    <textarea
                      value={respuesta}
                      onChange={(e) => setRespuesta(e.target.value)}
                      placeholder="Escribe tu respuesta..."
                      className="w-full p-3 border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      rows={4}
                    />
                    {adjuntos.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {adjuntos.map((adj, idx) => (
                          <div key={idx} className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-sm">
                            <Paperclip className="w-3 h-3" />
                            <span className="truncate max-w-[150px]">{adj.nombre}</span>
                            <button onClick={() => eliminarAdjunto(idx)} className="text-gray-500 hover:text-red-500">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <label className="cursor-pointer">
                        <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploadingFile} />
                        <span className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800">
                          {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                          Adjuntar archivo
                        </span>
                      </label>
                      <Button onClick={enviarRespuesta} disabled={enviando || !respuesta.trim()} className="gap-2">
                        {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Enviar
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No se pudo cargar el mensaje</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}


/* ===== Chat P2P unificado: todos los mensajes con una persona (sin filtro de curso) ===== */
function P2PUnifiedChat({ token, currentUserId, otro, onClose }) {
  const [mensajes, setMensajes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [spaceCtx, setSpaceCtx] = useState(null); // {id, nombre} para nuevo mensaje

  const fetchConv = useCallback(async () => {
    if (!otro?.otro_id) return;
    try {
      const r = await fetch(`${API_URL}/api/mensajes-internos/p2p/conversacion/${otro.otro_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      setMensajes(d.mensajes || []);
      // Tomar último space para usar como contexto por defecto al responder
      const ultMsg = [...(d.mensajes || [])].reverse().find(m => m.space_id);
      if (ultMsg) setSpaceCtx({ id: ultMsg.space_id, nombre: ultMsg.space_nombre });
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [otro, token]);

  useEffect(() => { fetchConv(); }, [fetchConv]);

  useEffect(() => {
    const i = setInterval(fetchConv, 15000);
    return () => clearInterval(i);
  }, [fetchConv]);

  const enviar = async () => {
    const t = texto.trim();
    if (!t || enviando) return;
    setEnviando(true);
    try {
      const r = await fetch(`${API_URL}/api/mensajes-internos/p2p/enviar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destinatario_id: otro.otro_id,
          contenido: t,
          space_id: spaceCtx?.id || null,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al enviar');
      }
      setTexto('');
      await fetchConv();
    } catch (e) {
      toast.error(e.message || 'No se pudo enviar el mensaje');
    } finally {
      setEnviando(false);
    }
  };

  const nombreOtro = `${otro?.nombre || ''} ${otro?.apellido || ''}`.trim() || otro?.email || 'Compañere';

  return (
    <div className="flex flex-col h-[600px]" data-testid="p2p-unified-chat">
      <div className="flex items-center gap-2 border-b pb-3 mb-3">
        <Button variant="ghost" size="sm" onClick={onClose}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver
        </Button>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{nombreOtro}</p>
          <p className="text-xs text-gray-500">{otro?.email}</p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : mensajes.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-8">Iniciá la conversación 👋</p>
        ) : mensajes.map((m, idx) => {
          const mio = m.remitente_id === currentUserId;
          const prev = mensajes[idx - 1];
          const showSpaceSeparator = m.space_nombre && (!prev || prev.space_id !== m.space_id);
          return (
            <div key={m.id}>
              {showSpaceSeparator && (
                <div className="text-center my-2">
                  <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                    <Users className="w-3 h-3 inline mr-1" />
                    {m.space_nombre}
                  </span>
                </div>
              )}
              <div className={`flex ${mio ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                  mio ? 'bg-primary text-white' : 'bg-gray-100 text-gray-900'
                }`}>
                  <p className="whitespace-pre-wrap break-words">{m.contenido}</p>
                  <p className={`text-[10px] mt-1 ${mio ? 'text-white/70' : 'text-gray-500'}`}>
                    {formatBsAs(m.created_at)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t pt-3 flex gap-2">
        <input
          value={texto}
          onChange={e => setTexto(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
          placeholder="Escribí tu mensaje…"
          disabled={enviando}
          className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          data-testid="p2p-chat-input"
        />
        <Button onClick={enviar} disabled={enviando || !texto.trim()} data-testid="p2p-chat-enviar-btn">
          {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
